type DeferredPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let deferredPrompt: DeferredPromptEvent | null = null;
const listeners = new Set<(p: DeferredPromptEvent | null) => void>();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as DeferredPromptEvent;
    for (const fn of listeners) fn(deferredPrompt);
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    for (const fn of listeners) fn(null);
  });
}

export function getDeferredPrompt(): DeferredPromptEvent | null {
  return deferredPrompt;
}

export function subscribeDeferredPrompt(fn: (p: DeferredPromptEvent | null) => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export async function triggerInstallPrompt(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferredPrompt) return 'unavailable';
  await deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice;
  deferredPrompt = null;
  for (const fn of listeners) fn(null);
  return choice.outcome;
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if ((navigator as { standalone?: boolean }).standalone === true) return true;
  return window.matchMedia('(display-mode: standalone)').matches;
}

export type Platform = 'ios-safari' | 'android-chrome' | 'desktop' | 'other';

export function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isIOS) return 'ios-safari';
  if (/Android/.test(ua)) return 'android-chrome';
  if (/Windows|Macintosh|Linux/.test(ua)) return 'desktop';
  return 'other';
}
